import PropTypes from "prop-types";
import React from "react";
import { useParams } from "react-router-dom";
import { defineMessages, FormattedMessage } from "react-intl";
import { Alert, Card, Link, SkeletonLoader, Title } from "@yoast/ui-library";
import { BackButton } from "../BackButton";
import useProvisionedSubscription from "../../hooks/useProvisionedSubscription";
import useProvisionerProducts from "../../hooks/useProvisionerProducts";
import { canSetSite, isActionable, productLabel } from "../../functions/provisionedSubscription";
import IncludedProductGroups from "./IncludedProductGroups";
import ProvisionedDate from "./ProvisionedDate";
import ProvisionedSubscriptionStatusBadge from "./ProvisionedSubscriptionStatusBadge";
import { provisionedSubscriptionShape } from "./provisionedSubscriptionShape";
import useProvisionedSubscriptionActions from "./useProvisionedSubscriptionActions";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.detail.title",
		defaultMessage: "Subscription {number}",
	},
	customerEmail: {
		id: "provisioning.detail.customerEmail",
		defaultMessage: "Customer email",
	},
	product: {
		id: "provisioning.detail.product",
		defaultMessage: "Product",
	},
	site: {
		id: "provisioning.detail.site",
		defaultMessage: "Site",
	},
	status: {
		id: "provisioning.detail.status",
		defaultMessage: "Status",
	},
	startDate: {
		id: "provisioning.detail.startDate",
		defaultMessage: "Start date",
	},
	endDate: {
		id: "provisioning.detail.endDate",
		defaultMessage: "End date",
	},
	downloads: {
		id: "provisioning.detail.downloads",
		defaultMessage: "Plugin downloads",
	},
	noDownloads: {
		id: "provisioning.detail.noDownloads",
		defaultMessage: "No downloads available.",
	},
	renew: {
		id: "provisioning.detail.action.renew",
		defaultMessage: "Extend with one billing term",
	},
	setSite: {
		id: "provisioning.detail.action.setSite",
		defaultMessage: "Connect site",
	},
	cancel: {
		id: "provisioning.detail.action.cancel",
		defaultMessage: "Cancel subscription",
	},
	refund: {
		id: "provisioning.detail.action.refund",
		defaultMessage: "Mark as refunded",
	},
	loadError: {
		id: "provisioning.detail.loadError",
		defaultMessage: "We could not load this subscription. It may not exist, or it may belong to another provisioner.",
	},
} );

/**
 * The file name at the end of a download URL, for a readable link.
 *
 * @param {string} url The download URL.
 *
 * @returns {string} The last path segment, or the URL when there is none.
 */
function downloadLabel( url ) {
	try {
		const segments = new URL( url ).pathname.split( "/" ).filter( Boolean );

		return segments[ segments.length - 1 ] || url;
	} catch {
		return url;
	}
}

/**
 * A text-link action under a field's value, as on the customer's subscription page.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The link button.
 */
function ActionLink( { message, onClick, variant = "primary" } ) {
	return (
		<Link as="button" type="button" variant={ variant } onClick={ onClick }>
			<FormattedMessage { ...message } />
		</Link>
	);
}

ActionLink.propTypes = {
	message: PropTypes.object.isRequired,
	onClick: PropTypes.func.isRequired,
	variant: PropTypes.string,
};

/**
 * The fields of one provisioned subscription, with its download links and, while it can still be
 * acted on, the action next to the field it changes.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The card.
 */
function ProvisionedSubscriptionCard( { subscription, actions = null } ) {
	const downloads = subscription.pluginDownloadUrls;
	// What the product includes lives on the products endpoint, matched on the code the subscription reports.
	const { products } = useProvisionerProducts();
	const product = products.find( candidate => candidate.productCode === subscription.product?.code );

	return (
		<Card className={ styles.detailCard }>
			<Card.Content>
				<dl className={ styles.detailList }>
					<dt><FormattedMessage { ...messages.customerEmail } /></dt>
					<dd>{ subscription.customerEmail || "—" }</dd>
					<dt><FormattedMessage { ...messages.product } /></dt>
					<dd>{ productLabel( subscription.product ) }</dd>
					<dt><FormattedMessage { ...messages.site } /></dt>
					<dd className={ styles.detailValue }>
						<span>{ subscription.siteUrl || "—" }</span>
						{ actions && canSetSite( subscription ) && (
							<ActionLink message={ messages.setSite } onClick={ () => actions.openSetSite( subscription ) } />
						) }
					</dd>
					<dt><FormattedMessage { ...messages.status } /></dt>
					<dd className={ styles.detailValue }>
						<span><ProvisionedSubscriptionStatusBadge status={ subscription.status } /></span>
						{ actions && (
							<span className={ styles.detailActionRow }>
								<ActionLink
									message={ messages.cancel }
									variant="error"
									onClick={ () => actions.openCancel( subscription ) }
								/>
								<ActionLink
									message={ messages.refund }
									variant="error"
									onClick={ () => actions.openRefund( subscription ) }
								/>
							</span>
						) }
					</dd>
					<dt><FormattedMessage { ...messages.startDate } /></dt>
					<dd><ProvisionedDate value={ subscription.startDate } /></dd>
					<dt><FormattedMessage { ...messages.endDate } /></dt>
					<dd className={ styles.detailValue }>
						<span><ProvisionedDate value={ subscription.endDate } /></span>
						{ actions && (
							<ActionLink message={ messages.renew } onClick={ () => actions.openRenew( subscription ) } />
						) }
					</dd>
					<dt><FormattedMessage { ...messages.downloads } /></dt>
					<dd>
						{ downloads.length === 0
							? <FormattedMessage { ...messages.noDownloads } />
							: (
								<div className={ styles.downloadLinks }>
									{ downloads.map( url => (
										<Link key={ url } href={ url } target="_blank" rel="noopener noreferrer">
											{ downloadLabel( url ) }
										</Link>
									) ) }
								</div>
							)
						}
					</dd>
				</dl>
				{ product && <IncludedProductGroups productGroups={ product.includes } /> }
			</Card.Content>
		</Card>
	);
}

ProvisionedSubscriptionCard.propTypes = {
	subscription: provisionedSubscriptionShape.isRequired,
	actions: PropTypes.shape( {
		openRenew: PropTypes.func.isRequired,
		openSetSite: PropTypes.func.isRequired,
		openCancel: PropTypes.func.isRequired,
		openRefund: PropTypes.func.isRequired,
	} ),
};

/**
 * Which of the page's three states applies, and the number to put in the heading meanwhile.
 *
 * @param {string}      id           The id from the URL.
 * @param {Object|null} subscription The subscription, once loaded.
 * @param {boolean}     isLoading    Whether the subscription is loading.
 * @param {boolean}     isError      Whether loading failed.
 *
 * @returns {{isMissing: boolean, isLoaded: boolean, number: string}} The state.
 */
function pageState( id, subscription, isLoading, isError ) {
	const isMissing = isError || ( ! isLoading && ! subscription );

	return {
		isMissing,
		isLoaded: ! isMissing && ! isLoading,
		number: subscription ? ( subscription.subscriptionNumber || subscription.id ) : id,
	};
}

/**
 * One provisioned subscription, with the same actions as the list's row menu.
 *
 * @returns {JSX.Element} The page.
 */
function ProvisionedSubscriptionDetailPage() {
	const { id } = useParams();
	const { subscription, isLoading, isError } = useProvisionedSubscription( id );
	const actions = useProvisionedSubscriptionActions();

	const { isMissing, isLoaded, number } = pageState( id, subscription, isLoading, isError );
	const cardActions = isLoaded && isActionable( subscription ) ? actions : null;

	return (
		<div>
			<BackButton />
			<section className={ styles.section }>
				<Title as="h1" size="2">
					<FormattedMessage { ...messages.title } values={ { number } } />
				</Title>
				{ isMissing && (
					<Alert variant="error" role="alert">
						<FormattedMessage { ...messages.loadError } />
					</Alert>
				) }
				{ ! isMissing && isLoading && <SkeletonLoader>Loading subscription…</SkeletonLoader> }
				{ isLoaded && <ProvisionedSubscriptionCard subscription={ subscription } actions={ cardActions } /> }
			</section>
			{ actions.modals }
		</div>
	);
}

export default ProvisionedSubscriptionDetailPage;
